/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Card } from '@mui/material';
import { isEqual } from 'lodash';
import React from 'react';
import type { PlotPreset, TimeSeriesService } from '@opengeoweb/shared';
import { AlertBanner } from '@opengeoweb/shared';
import type { TimeSliderStartEndStep } from '@opengeoweb/store';
import { getSharedTimesteps } from './chartUtils';
import { useCurrentTime } from './CurrentTime';
import { TimeSeriesChart, useChartSynchronization } from './TimeSeriesChart';
import type {
  PointerLocation,
  GeoJsonWithService,
  FailedParameterInfo,
  PlotWithData,
} from './types';
import { useTimeseriesTranslation } from '../../utils/i18n';
import { useGetPlotsWithData } from '../../utils/timeseries-api/hooks';
import { TimeSeriesPanelsConnect } from '../TimeSeriesPanels/TimeSeriesPanelsConnect';

const getParameterKey = (param: FailedParameterInfo): string => {
  return `${param.serviceId}|${param.collectionId}|${param.propertyName}`;
};

const getErrorTitle = (parameters: FailedParameterInfo[]): string => {
  const propertyNames = parameters.map((param) => param.propertyName);
  if (propertyNames.length > 1) {
    return `"${propertyNames.join('", "')}"`;
  }
  return `"${propertyNames[0] || ''}"`;
};

const getErrorTranslationKey = (
  errorKey: string,
  parameterCount: number,
): string => {
  // Use plural translation only when multiple parameters fail with error-message-general
  return errorKey === 'error-message-general' && parameterCount > 1
    ? 'error-message-general-plural'
    : errorKey;
};

const TimeSeriesPlot: React.FC<{
  plot: PlotWithData;
  parametersFailingInAllPlots: FailedParameterInfo[];
  synchronization: ReturnType<typeof useChartSynchronization>;
  sharedTimesteps: Date[];
  currentTime: number;
}> = ({
  plot,
  parametersFailingInAllPlots,
  synchronization,
  sharedTimesteps,
  currentTime,
}) => {
  const { t } = useTimeseriesTranslation();
  const commonKeys = new Set(parametersFailingInAllPlots.map(getParameterKey));
  const failedParameters =
    plot.failedParameters?.filter(
      (parameter) => !commonKeys.has(getParameterKey(parameter)),
    ) ?? [];
  const errorKey = plot.errors?.[0] || 'error-message-general';
  const errorInfo = t(
    getErrorTranslationKey(errorKey, failedParameters.length),
  );
  const errorTitle = getErrorTitle(failedParameters);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
      }}
    >
      {!!failedParameters.length && (
        <AlertBanner info={errorInfo} shouldClose title={errorTitle} />
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <TimeSeriesChart
          currentTime={currentTime}
          fitToContainer
          plotsWithData={[plot]}
          sharedTimesteps={sharedTimesteps}
          shouldShowLegend={false}
          shouldShowZoom={false}
          synchronization={synchronization}
        />
      </Box>
    </Box>
  );
};

interface TimeSeriesViewProps {
  viewId: string;
  plotPreset: PlotPreset;
  selectedLocation: PointerLocation;
  services: TimeSeriesService[];
  splitGeoJsonByCollection: GeoJsonWithService[];
  isLoading?: (startOrFinish: boolean) => void;
  shouldShowLegend?: boolean;
  onToggleLegendParam?: (legendId: string) => void;
  syncgroupTimeState?: TimeSliderStartEndStep;
}

export const TimeSeriesView: React.FC<TimeSeriesViewProps> = React.memo(
  ({
    viewId,
    plotPreset,
    selectedLocation,
    services,
    splitGeoJsonByCollection,
    isLoading,
    shouldShowLegend = true,
    onToggleLegendParam,
    syncgroupTimeState,
  }: TimeSeriesViewProps) => {
    const { t } = useTimeseriesTranslation();
    const synchronization = useChartSynchronization();
    const currentTime = useCurrentTime();
    const { data: plotsWithData, isFetching: isFetchingPlots } =
      useGetPlotsWithData(
        plotPreset,
        selectedLocation,
        services,
        splitGeoJsonByCollection,
        t,
        syncgroupTimeState,
      );
    const sharedTimesteps = React.useMemo(
      () => getSharedTimesteps(plotsWithData ?? []),
      [plotsWithData],
    );

    React.useEffect(() => {
      if (isLoading) {
        isLoading(isFetchingPlots);
      }
    }, [isFetchingPlots, isLoading]);

    const getCombinedErrorInfo = React.useCallback(
      (
        parametersFailingInAllPlots: FailedParameterInfo[],
        plotsWithData: { errors?: string[] }[] | undefined,
        totalPlotCount: number,
      ): string => {
        if (parametersFailingInAllPlots.length === 0 || totalPlotCount === 0) {
          return '';
        }

        const errorKey =
          plotsWithData?.[0]?.errors?.[0] || 'error-message-general';
        const translationKey = getErrorTranslationKey(
          errorKey,
          parametersFailingInAllPlots.length,
        );
        const errorMessage = t(translationKey);

        return totalPlotCount === 1
          ? errorMessage
          : `${t('all-plots')}: ${errorMessage}`;
      },
      [t],
    );

    const getParametersFailingInAllPlots = (
      plotsWithData: { failedParameters?: FailedParameterInfo[] }[] | undefined,
      totalPlotCount: number,
    ): FailedParameterInfo[] => {
      if (!plotsWithData || totalPlotCount === 0) {
        return [];
      }

      const parameterCounts: Record<
        string,
        { count: number; param: FailedParameterInfo }
      > = {};
      plotsWithData.forEach((plot) => {
        plot.failedParameters?.forEach((param) => {
          const key = getParameterKey(param);
          if (parameterCounts[key]) {
            parameterCounts[key].count += 1;
          } else {
            parameterCounts[key] = { count: 1, param };
          }
        });
      });

      return Object.values(parameterCounts)
        .filter(({ count }) => count === totalPlotCount)
        .map(({ param }) => param);
    };

    const totalPlotCount = plotsWithData?.length || 0;
    const parametersFailingInAllPlots: FailedParameterInfo[] = React.useMemo(
      () => getParametersFailingInAllPlots(plotsWithData, totalPlotCount),
      [plotsWithData, totalPlotCount],
    );

    const hasParametersFailingInAllPlots =
      parametersFailingInAllPlots.length > 0;
    const combinedErrorTitle = getErrorTitle(parametersFailingInAllPlots);
    const combinedErrorInfo = React.useMemo(
      () =>
        hasParametersFailingInAllPlots
          ? getCombinedErrorInfo(
              parametersFailingInAllPlots,
              plotsWithData,
              totalPlotCount,
            )
          : '',
      [
        getCombinedErrorInfo,
        hasParametersFailingInAllPlots,
        parametersFailingInAllPlots,
        plotsWithData,
        totalPlotCount,
      ],
    );

    const renderPlot = (plotId: string): React.ReactNode => {
      const plot = plotsWithData?.find((item) => item.plotId === plotId);
      return plot ? (
        <TimeSeriesPlot
          currentTime={currentTime}
          parametersFailingInAllPlots={parametersFailingInAllPlots}
          plot={plot}
          sharedTimesteps={sharedTimesteps}
          synchronization={synchronization}
        />
      ) : null;
    };

    return (
      <Card
        data-testid="TimeSeriesView"
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {hasParametersFailingInAllPlots ? (
          <Card
            sx={{
              border: 'solid 1px',
              borderColor: 'geowebColors.cards.cardContainerBorder',
              boxShadow: 'none',
            }}
          >
            <Box data-testid="TimeSeriesView-alert-banner">
              <AlertBanner
                info={combinedErrorInfo}
                shouldClose
                title={combinedErrorTitle}
              />
            </Box>
          </Card>
        ) : null}
        {!!plotsWithData?.length && !!shouldShowLegend && (
          <Box sx={{ flex: '0 0 auto', mb: -1 }}>
            <TimeSeriesChart
              controlsOnly="legend"
              onToggleLegendParam={onToggleLegendParam}
              plotsWithData={plotsWithData}
              shouldShowZoom={false}
            />
          </Box>
        )}
        {!!plotsWithData?.length && (
          <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'auto' }}>
            <TimeSeriesPanelsConnect renderPlot={renderPlot} viewId={viewId} />
          </Box>
        )}
        {!!plotsWithData?.length && (
          <Box sx={{ flex: '0 0 auto' }}>
            <TimeSeriesChart
              controlsOnly="zoom"
              currentTime={currentTime}
              plotsWithData={plotsWithData}
              sharedTimesteps={sharedTimesteps}
              shouldShowLegend={false}
              synchronization={synchronization}
            />
          </Box>
        )}
      </Card>
    );
  },
  (previousProps, nextProps) => {
    const sameView = previousProps.viewId === nextProps.viewId;
    const sameCallbacks =
      previousProps.isLoading === nextProps.isLoading &&
      previousProps.onToggleLegendParam === nextProps.onToggleLegendParam;
    const samePreset = previousProps.plotPreset === nextProps.plotPreset;
    const sameLocation = isEqual(
      previousProps.selectedLocation,
      nextProps.selectedLocation,
    );
    const sameGeoJson = isEqual(
      previousProps.splitGeoJsonByCollection,
      nextProps.splitGeoJsonByCollection,
    );
    const sameLegend =
      previousProps.shouldShowLegend === nextProps.shouldShowLegend;
    const sameServices = isEqual(previousProps.services, nextProps.services);
    const sameTime =
      previousProps.syncgroupTimeState?.start ===
        nextProps.syncgroupTimeState?.start &&
      previousProps.syncgroupTimeState?.end ===
        nextProps.syncgroupTimeState?.end;

    return (
      sameView &&
      sameCallbacks &&
      samePreset &&
      sameLocation &&
      sameGeoJson &&
      sameLegend &&
      sameServices &&
      sameTime
    );
  },
);
